import React, {useContext, useState} from "react";
import {
    ContentContext,
    ContentTypeContext,
    CountryContext,
    YearContext
} from "@/components/ContentElements/News/Provider/NewsFilterProvider";
import clsx from "clsx";
import IconRenderer from "@/libs/contentful/helper/IconRenderer";
import {useTranslations} from "next-intl";
import {
    numberOfContentTagsContext,
    NumberOfContentTypesContext,
    NumberOfCountriesContext,
    NumberOfYearsContext
} from "@/components/ContentElements/News/Provider/NewsCounterProvider";

interface Props {
    contentTagsHeadline: string
    contentTypeHeadline: string
    countryHeadline: string
    yearHeadline: string
}

export default function FilterElements({contentTagsHeadline, contentTypeHeadline, countryHeadline, yearHeadline}: Props) {
    const t = useTranslations("News");
    const { filterContent, setContent } = useContext(ContentContext);
    const { filterContentType, setContentType } = useContext(ContentTypeContext);
    const { filterCountry, setCountry } = useContext(CountryContext);
    const { filterYear, setYear } = useContext(YearContext);
    const { numberOfContentTags } = useContext(numberOfContentTagsContext);
    const { numberOfContentTypes } = useContext(NumberOfContentTypesContext);
    const { numberOfCountries } = useContext(NumberOfCountriesContext);
    const { numberOfYears } = useContext(NumberOfYearsContext);

    const handleFilterChange = (setter:Function) => (e:React.ChangeEvent<HTMLInputElement>) => {
        if (!document.startViewTransition) {
            setter(e.target.value)
            return;
        }
        document.startViewTransition(() => setter(e.target.value));
    }

    const resetFilter = () => {
        if (!document.startViewTransition) {
            setContent('')
            setContentType('')
            setCountry('')
            setYear('')
            document.querySelectorAll<HTMLInputElement>('fieldset').forEach(fieldset => {
                fieldset.querySelectorAll('input').forEach(input => input.checked = false)
            })
            return;
        }
        document.startViewTransition(() => setContent(''))
        document.startViewTransition(() => setContentType(''))
        document.startViewTransition(() => setCountry(''))
        document.startViewTransition(() => setYear(''))
        document.querySelectorAll<HTMLInputElement>('fieldset').forEach(fieldset => {
            fieldset.querySelectorAll('input').forEach(input => input.checked = false)
        })
    }

    const inputFields = [
        {
            name: contentTagsHeadline,
            getter: filterContent,
            setter: setContent,
            options: Object.keys(numberOfContentTags),
            numberOfNews: numberOfContentTags
        },
        {
            name: contentTypeHeadline,
            getter: filterContentType,
            setter: setContentType,
            options: Object.keys(numberOfContentTypes),
            numberOfNews: numberOfContentTypes
        },
        {
            name: countryHeadline,
            getter: filterCountry,
            setter: setCountry,
            options: Object.keys(numberOfCountries),
            numberOfNews: numberOfCountries
        },
        {
            name: yearHeadline,
            getter: filterYear,
            setter: setYear,
            options: Object.keys(numberOfYears).reverse(),
            numberOfNews: numberOfYears
        }
    ]
    return (
        <>
            {inputFields.map((field, index) => {
                const [isOpen, toggle] = useState(false)

                const handleClick = (e: React.MouseEvent<HTMLButtonElement>, inputId:string) => {
                    toggle(false)
                    document.getElementById(inputId)?.click()
                    const parent = e.currentTarget.closest('.filter') as HTMLElement
                    (parent.querySelector('.filter-label') as HTMLButtonElement)?.focus()
                }

                return (
                    <div key={index} className="flex-1 bg-white rounded-[2px] relative filter" onBlur={(e) => !e.currentTarget.contains(e.relatedTarget) ? toggle(false) : null}>
                        <button className={clsx(isOpen ? 'border-b-transparent' : 'border-b-black', "filter-label border-[1px] text-p px-m py-s relative hover:bg-brand-hover cursor-pointer w-full text-left focus:outline focus:outline-2 focus:outline-outline focus:bg-brand-hover")}
                                aria-controls={'filter-' + index}
                                aria-expanded={isOpen}
                                onClick={() => toggle(!isOpen)}>
                                    {field.getter !== '' ? field.getter : field.name}
                                    <IconRenderer icon="icon-chevron-down" className="absolute right-[15px] top-[50%] translate-y-[-50%]" />
                        </button>
                        <ul id={'filter-' + index} className={clsx(isOpen ? 'absolute' : 'hidden', 'border-[1px] border-t-[0px] bg-white list-none m-[0] w-full appearance-none text-p z-[1]')}>
                            {field.options.map((option: string | number, index) => (
                                <li key={index}
                                    className="hover:bg-brand-hover border-t-[1px] border-brand-light-cool-grey">
                                    <button key={index}
                                        className="w-full h-full focus:outline focus:outline-2 focus:outline-outline focus:bg-brand-hover text-left"
                                        type="button"
                                        onClick={(e) => handleClick(e, field.name + '-option-' + index)}>
                                        <label className="block cursor-pointer px-m py-s w-full"
                                               htmlFor={field.name + '-option-' + index}>
                                            {option}
                                            <span className="text-black/60"> ({field.numberOfNews[String(option)]})</span>
                                            <input onChange={handleFilterChange(field.setter)}
                                                   type="radio"
                                                   id={field.name + '-option-' + index}
                                                   value={option}
                                                   name={field.name}
                                                   className="hidden" />
                                        </label>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )
            })}
            <button type="button"
                    disabled={!filterContent && !filterContentType && !filterCountry && !filterYear}
                    className={clsx(filterContent || filterContentType || filterCountry || filterYear ? 'text-black' : 'text-black-40', "m-auto w-fit text-p font-sans md:whitespace-pre-wrap font-bold underline")}
                    onClick={resetFilter}>
                {t("resetFilter")}
            </button>
        </>
    )
}